import { DrawData, GcProgressItem, SovItem } from "../types/inspector.types";

const GC_STATUS_LABEL: Record<string, string> = {
  in_progress: "In Progress",
  not_started: "Not Started",
  completed: "Complete",
  nearing_completion: "Nearing Completion",
};

const GC_STATUS_CLASS: Record<string, string> = {
  in_progress: "gcProgressAmber",
  not_started: "gcProgressGray",
  completed: "gcProgressGreen",
  nearing_completion: "gcProgressYellow",
};

interface DrawTabProps {
  drawData: DrawData;
  sovItems: SovItem[];
  gcProgress?: GcProgressItem[];
  drawPercentage: string;
  cscEstimation: string;
  flagNotes: string;
}

export default function DrawTab({
  drawData,
  sovItems,
  gcProgress,
  drawPercentage,
  cscEstimation,
  flagNotes,
}: DrawTabProps) {
  const sym = drawData.currencySymbol;
  const eTotal = sovItems.reduce((s, i) => s + (i.thisDrawNum ?? 0), 0);
  const fTotal = sovItems.reduce((s, i) => s + (i.storedNum ?? 0), 0);
  const fmt = (n: number) =>
    `${sym}${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <>
      <div className="readOnlyNotice">
        <span>🔒</span>
        <span>
          Read-only · Current draw data from GC submission · Use FI Form tab to
          record field findings
        </span>
      </div>
      <div className="drawSumCard">
        <div className="dscHeader">
          <div>
            <div className="dscHeaderTitle">
              AIA G702 · Application #{drawData.applicationNumber}
            </div>
            <div className="dscHeaderSub">
              {drawData.projectName}
              {drawData.period ? ` · Period to ${drawData.period}` : ""}
            </div>
          </div>
          <div className="dscHeaderAmt">{drawData.currentPaymentDue}</div>
        </div>
        {drawData.rows.map((row) => (
          <div
            key={row.num}
            className={`drawSumRow${row.isTotal ? " total" : ""}`}
          >
            <span>
              <span className="dsrNum">{row.num}</span>
              <span className={`dsrLabel${row.isTotal ? " bold" : ""}`}>
                {row.label}
              </span>
            </span>
            <span
              className={`dsrVal${row.color ? ` ${row.color}` : ""}${row.isTotal ? " large" : ""}`}
            >
              {row.val ? row.val : "-"}
            </span>
          </div>
        ))}
      </div>
      {(drawPercentage ||
        cscEstimation ||
        flagNotes ||
        sovItems.some((i) => i.flagged)) && (
        <div className="inspectorContext">
          <div className="inspectorContextTitle">Inspector Context</div>
          <div>
            {drawPercentage && (
              <>
                GC is billing <strong>{drawPercentage}</strong> of
                contract.{" "}
              </>
            )}
            {cscEstimation && (
              <>
                CSC estimate: <strong>~{cscEstimation}%</strong>.{" "}
              </>
            )}
            {sovItems.some((i) => i.flagged) &&
              (() => {
                const flagged = sovItems.filter((i) => i.flagged);
                return (
                  <>
                    {flagged.length} line item{flagged.length > 1 ? "s" : ""}{" "}
                    pre-flagged ({flagged.map((i) => i.desc).join(", ")}).{" "}
                  </>
                );
              })()}
            {flagNotes}
          </div>
        </div>
      )}
      <div className="drawSumCard">
        <div className="g703SectionTitle">
          G703 — This Draw's Line Items (Col E + F)
        </div>
        <div className="g703Scroll">
          <div className="g703ColHeaders">
            <span className="g703ColCode">Code</span>
            <span className="g703ColDesc">Description</span>
            <span className="g703ColPeriod">This Period (E)</span>
            <span className="g703ColStored">Stored (F)</span>
            <span className="g703ColPct">% (G/C)</span>
          </div>
          {sovItems.map((item) => (
            <div
              key={item.id}
              className={`g703Row${item.flagged ? " flagged" : ""}`}
            >
              <span className={`g703Code${item.flagged ? " flagged" : ""}`}>
                {item.code}
              </span>
              <div className="g703DescWrap">
                <div className={`g703DescName${item.flagged ? " flagged" : ""}`}>
                  {item.desc}
                  {item.flagged ? " ⚠" : ""}
                </div>
                {item.sub && (
                  <div className={`g703DescSub${item.flagged ? " flagged" : ""}`}>
                    {item.sub}
                  </div>
                )}
              </div>
              <span className={`g703Value${item.flagged ? " flagged" : ""}`}>
                {item.sovValue && (
                  <span className="g703SovLabel">SOV {item.sovValue}</span>
                )}
                {item.thisDraw}
              </span>
              <span className={`g703Stored${item.storedNum ? " hasValue" : ""}`}>
                {item.storedFormatted ?? "—"}
              </span>
              <span className={`g703Pct${item.flagged ? " flagged" : ""}`}>
                {item.claim}
              </span>
            </div>
          ))}
          <div className="g703Footer">
            <span>Total This Draw (E+F)</span>
            <span className="g703FooterTotal">
              {fmt(eTotal)}
              {fTotal > 0 ? ` + ${fmt(fTotal)} stored` : ""}
            </span>
          </div>
        </div>
      </div>

      {gcProgress && gcProgress.length > 0 && (
        <div className="drawSumCard">
          <div className="gcProgressHeader">
            GC Self-Reported Progress (from Draw Submission)
          </div>
          {gcProgress.map((item, idx) => (
            <div className="gcProgressRow" key={item.id ?? idx}>
              <span className="gcProgressNum">{idx + 1}</span>
              <div className="gcProgressInfo">
                <div className="gcProgressName">
                  {item.aiaItem?.itemName ?? ""}
                </div>
                <div className="gcProgressCode">
                  {item.aiaItem?.itemLabel ?? ""}
                </div>
              </div>
              <span
                className={`gcProgressBadge ${GC_STATUS_CLASS[item.status ?? ""] ?? "gcProgressGray"}`}
              >
                {GC_STATUS_LABEL[item.status ?? ""] ?? item.status ?? "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
